import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import {
  ArrowLeft,
  Search,
  BookOpen,
  Monitor,
  PlayCircle,
  Star,
  Filter,
  ArrowRight
} from "lucide-react";
import { motion } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';
import PublicNavbar from "../components/PublicNavbar";

export default function CategoryPage() {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        const [cRes, eRes] = await Promise.all([
          api.get("/courses"),
          api.get("/exams/public/list")
        ]);

        // Filter by category
        const filteredCourses = cRes.data.filter(c =>
          (c.category || "General").toLowerCase() === categoryName.toLowerCase()
        );
        const filteredExams = eRes.data.filter(e =>
          (e.category || "General").toLowerCase() === categoryName.toLowerCase()
        );

        setCourses(filteredCourses);
        setExams(filteredExams);
      } catch (err) {
        toast.error("Error loading category content");
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryData();
  }, [categoryName]);

  return (
    <div className="min-h-screen bg-slate-50 font-['Inter',sans-serif]">
      <Toaster position="top-center" />

      <PublicNavbar />

      <main className="max-w-7xl mx-auto px-4 py-12">
        {/* HERO */}
        <div className="mb-16">
          <div className="bg-indigo-600 rounded-[3rem] p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-white/10 skew-x-12 translate-x-20" />
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-4xl lg:text-5xl font-black mb-6 leading-tight">Master {categoryName} <br />With Expert Resources</h2>
              <p className="text-indigo-100 text-lg font-medium opacity-80">
                Explore our comprehensive collection of video courses and full-length mock tests designed to help you excel.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-20">

            {/* EXAMS SECTION */}
            <section>
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                  <Monitor className="text-indigo-600" /> Mock Test Series
                </h3>
                <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">{exams.length} Available</span>
              </div>

              {exams.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {exams.map((exam, i) => (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      key={i}
                      className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl transition-all"
                    >
                      <div className="p-3 bg-slate-50 rounded-xl w-fit mb-6"><BookOpen size={20} className="text-indigo-600" /></div>
                      <h4 className="font-black text-slate-900 mb-2 line-clamp-2 min-h-[3rem]">{exam.title}</h4>
                      <p className="text-xs font-bold text-slate-400 uppercase mb-6">{exam.questions?.length || 0} Questions • {exam.duration_minutes} Mins</p>
                      <button
                        onClick={() => {
                          if (localStorage.getItem("token")) {
                            navigate("/exams");
                          } else {
                            navigate("/login");
                          }
                        }}
                        className="w-full py-3 bg-indigo-50 text-indigo-600 font-black rounded-xl hover:bg-indigo-600 hover:text-white transition-all"
                      >
                        Start Test
                      </button>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2rem] p-20 text-center text-slate-400 font-bold uppercase tracking-widest italic">
                  No exams found in this category
                </div>
              )}
            </section>

            {/* COURSES SECTION */}
            <section>
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                  <PlayCircle className="text-indigo-600" /> Video Courses
                </h3>
                <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">{courses.length} Available</span>
              </div>

              {courses.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {courses.map((course, i) => (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      key={i}
                      className="bg-white rounded-[2.5rem] border border-slate-100 overflow-hidden hover:shadow-xl transition-all group"
                    >
                      <div className="aspect-video bg-slate-100 relative">
                        {course.thumbnail && <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />}
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-all flex items-center justify-center">
                          <PlayCircle className="text-white opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100 transition-all" size={48} />
                        </div>
                      </div>
                      <div className="p-8">
                        <h4 className="text-lg font-black text-slate-900 mb-4 line-clamp-2 min-h-[3.5rem] group-hover:text-indigo-600 transition-colors">{course.title}</h4>
                        <div className="flex items-center gap-2 mb-8 text-amber-500">
                          <Star size={14} fill="currentColor" />
                          <span className="text-sm font-black text-slate-900">4.9</span>
                        </div>
                        <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                          <p className="text-2xl font-black text-slate-900">₹{course.price}</p>
                          <button
                            onClick={() => {
                              if (localStorage.getItem("token")) {
                                navigate(`/course-detail/${course.id}`);
                              } else {
                                navigate("/login");
                              }
                            }}
                            className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-black rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                          >
                            Enroll <ArrowRight size={18} />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2rem] p-20 text-center text-slate-400 font-bold uppercase tracking-widest italic">
                  No courses found in this category
                </div>
              )}
            </section>

          </div>
        )}
      </main>

      {/* FOOTER */}
      <footer className="bg-slate-900 py-16 text-white mt-32">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-black mb-4">Ready to start your journey?</h2>
          <p className="text-slate-400 mb-10 font-medium">Join thousands of students achieving their goals today.</p>
          <button onClick={() => navigate('/register')} className="px-10 py-4 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 hover:bg-indigo-700 transition-all">
            Create Free Account
          </button>
        </div>
      </footer>
    </div>
  );
}
